import {
  SelectOption,
  toWSMoneyString,
  useIsDesktop,
  useIsMobile,
  useIsTablet,
  useModalContext,
  WSButton,
  WSButtons,
  WSCheckboxToggle,
  WSDivider,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSFlexGrid,
  WSForm,
  WSIcon,
  WSModal,
  WSScreen,
  WSSelect,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import { IInvoiceRequestBase } from "@wingspanhq/payments/dist/interfaces/api/invoice";
import { formatMoney } from "accounting";
import React, { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import * as Yup from "yup";
import { Overlay } from "../../../components/Overlay";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import {
  useIntegrationsQuickbooks,
  useIntegrationsQuickbooksAccountExpenses,
  useIntegrationsQuickbooksItems
} from "../../../query/integrations/queries";
import { isQuickbooksServiceActive } from "../../../query/integrations/selectors";
import { useMemberClientsQuery } from "../../../query/payments/queries";
import { getMemberClient } from "../../../query/payments/selectors";
import {
  INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT,
  QUICKBOOKS_ENTITY,
  RefreshModal
} from "../../../Settings/screens/Integrations/quickbooks/RefreshButton";
import {
  nullableNumberValidation,
  number,
  positiveNumber
} from "../../../utils/customValidations";
import { timeToQuantity } from "../../utils";
import {
  InvoicesFormStep1Values,
  useInvoicesFormContext
} from "./InvoicesForm";
import styles from "./LineItemsSection.module.scss";

export type LineItemValues = {
  description: string;
  totalCost: string;
  rate: "fixed" | "hourly" | "quantity" | "other";
  costPerUnit: string;
  quantity: string;
  minutes: string;
  customUnit: string;
  discount: {
    amount: string;
    percentage: string;
    type: "amount" | "percentage";
    description: string;
  };
  integration?: IInvoiceRequestBase["integration"];
  remove: string;
  reimbursableExpense: boolean;
};

export type LineItemsSectionValues = {
  lineItems: LineItemValues[];
};

type Props = {
  isIntegrationQuickbooksItems?: boolean;
  isIntegrationQuickbooksAccounts?: boolean;
  disabled?: boolean;
} & WSElementProps;

export const lineItemValidationSchema = Yup.lazy((lineItem: any) => {
  if (lineItem.remove) {
    return Yup.object();
  } else {
    return Yup.object().shape({
      description: Yup.string().required("Required"),
      totalCost: Yup.number()
        .typeError("Required")
        .required("Required"),
      rate: Yup.string().oneOf(["fixed", "hourly", "quantity", "other"]),
      costPerUnit: nullableNumberValidation.when("rate", {
        is: rate => {
          return rate !== "fixed";
        },
        then: positiveNumber.required("Required")
      }),
      quantity: nullableNumberValidation.when("rate", {
        is: rate => {
          return rate !== "fixed";
        },
        then: number
      }),
      minutes: nullableNumberValidation.when("rate", {
        is: "hourly",
        then: number
      }),
      customUnit: Yup.string().when("rate", {
        is: "other",
        then: Yup.string().required("Required")
      }),
      reimbursableExpense: Yup.boolean()
    });
  }
});

const calculateAmount = (values: LineItemValues) => {
  const quantity =
    values.rate === "hourly"
      ? timeToQuantity(Number(values.quantity), Number(values.minutes))
      : Number(values.quantity);

  return Number(values.costPerUnit) * quantity;
};

export const calculateTotalAmount = (lineItems: LineItemValues[]) =>
  lineItems
    ? lineItems.reduce((total, lineItem) => {
        if (lineItem.remove) {
          return total;
        } else {
          const initialCost = Number(lineItem.totalCost);
          const discountPercentage = Number(
            lineItem?.discount?.percentage || 0
          );
          const discountAmount = Number(lineItem?.discount?.amount || 0);
          const discounType = lineItem?.discount?.type || "percentage";
          const finalCost =
            discounType === "percentage" && discountPercentage > 0
              ? initialCost - initialCost * (discountPercentage / 100)
              : discounType === "amount" && discountAmount > 0
              ? initialCost - discountAmount
              : initialCost;
          return total + finalCost;
        }
      }, 0)
    : 0;

export const calculateTotalAmountWithoutDiscount = (
  lineItems: LineItemValues[]
) =>
  lineItems
    ? lineItems.reduce((total, lineItem) => {
        if (lineItem.remove) {
          return total;
        } else {
          return total + Number(lineItem.totalCost);
        }
      }, 0)
    : 0;

export const LineItemsSection: React.FC<Props> = ({
  isIntegrationQuickbooksAccounts,
  isIntegrationQuickbooksItems,
  disabled,
  ...elementProps
}) => {
  const { openModal, closeModal } = useModalContext();
  const { control, register, trigger, setValue } = useFormContext();
  const { fields: lineItems, append, remove, insert } = useFieldArray<
    LineItemValues
  >({
    control,
    name: "lineItems"
  });
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();
  const { getValues } = useFormContext<InvoicesFormStep1Values>();

  const { invoice, values } = useInvoicesFormContext();

  const memberClientsQuery = useMemberClientsQuery({
    enabled: values?.client.memberClientId
  });

  const memberClient =
    memberClientsQuery.data && values?.client.memberClientId
      ? getMemberClient(memberClientsQuery.data, values?.client.memberClientId)
      : undefined;

  const qIntegrationsQuickbooks = useIntegrationsQuickbooks({
    enabled: isIntegrationQuickbooksAccounts || isIntegrationQuickbooksItems
  });

  const qFeatureFlags = useFeatureFlags();

  const isQuickbooksActive =
    qFeatureFlags.data?.qboOnboarding &&
    qIntegrationsQuickbooks.data &&
    isQuickbooksServiceActive(qIntegrationsQuickbooks.data);

  const qIntegrationsQuickbooksItems = useIntegrationsQuickbooksItems({
    enabled: isQuickbooksActive && isIntegrationQuickbooksItems
  });

  const qIntegrationsQuickbooksAccountExpenses = useIntegrationsQuickbooksAccountExpenses(
    {
      enabled: isQuickbooksActive && isIntegrationQuickbooksAccounts
    }
  );

  const quickbooksOptions = isIntegrationQuickbooksItems
    ? qIntegrationsQuickbooksItems.data?.map(item => ({
        label: item.name,
        value: item.itemId
      }))
    : isIntegrationQuickbooksAccounts
    ? qIntegrationsQuickbooksAccountExpenses.data?.map(item => ({
        label: item.name,
        value: item.accountId
      }))
    : [];

  useEffect(() => {
    if (isMobile || isTablet) {
      const values = getValues();

      if (values.lineItems?.[0]?.totalCost === "") {
        remove(0);
      }
    }
  }, [isMobile, isTablet, getValues, remove]);

  const isQuickbooksItem = isIntegrationQuickbooksItems && isQuickbooksActive;
  const isQuickbooksAccount =
    isIntegrationQuickbooksAccounts && isQuickbooksActive;
  const quickbooksDefaultItemId = isQuickbooksItem
    ? invoice?.integration?.quickbooks?.itemId ||
      memberClient?.integration?.quickbooks?.itemId ||
      qIntegrationsQuickbooks.data?.defaults?.itemId
    : undefined;
  const quickbooksDefaultAccountId = isQuickbooksAccount
    ? invoice?.integration?.quickbooks?.expenseAccountId ||
      memberClient?.integration?.quickbooks?.expenseAccountId ||
      qIntegrationsQuickbooks.data?.defaults?.expenseAccountId
    : undefined;

  const quickbooksProps = {
    isQuickbooksItem,
    isQuickbooksAccount,
    quickbooksOptions,
    quickbooksDefaultItemId,
    quickbooksDefaultAccountId
  };

  return (
    <WSElement {...elementProps}>
      {disabled && <Overlay white overParent />}
      <RefreshModal />
      <WSModal name="line-item" size="S">
        {(props?: { index: number; lineItem: LineItemValues }) => (
          <LineItemFormMobile
            onCancel={() => {
              closeModal("line-item");
            }}
            onSubmit={values => {
              if (props?.lineItem && props.index !== undefined) {
                remove(props.index);
                insert(props.index, values);
              } else {
                append(values);
              }

              trigger("lineItems");

              closeModal("line-item");
            }}
            defaultValues={props?.lineItem}
            {...quickbooksProps}
          />
        )}
      </WSModal>
      <WSElement mb="XS">
        <WSFlexGrid container spacing="S">
          <WSFlexGrid item mobile="6" tablet="7">
            <WSText.Label color="grey600">Description</WSText.Label>
          </WSFlexGrid>
          <WSFlexGrid item mobile="6" tablet="5">
            <WSText.Label color="grey600">Amount</WSText.Label>
          </WSFlexGrid>
        </WSFlexGrid>
      </WSElement>
      <WSDivider mb="S" />
      <WSScreen.MobileAndTablet>
        {lineItems.map((lineItem, index) => (
          <WSElement my="S" key={lineItem.id} hidden={!!lineItem.remove}>
            <input
              type="hidden"
              ref={register()}
              name={`lineItems[${index}].description`}
              defaultValue={lineItem.description}
            />
            <input
              type="hidden"
              ref={register()}
              name={`lineItems[${index}].totalCost`}
              defaultValue={lineItem.totalCost}
            />
            <input
              type="hidden"
              ref={register()}
              name={`lineItems[${index}].rate`}
              defaultValue={lineItem.rate}
            />
            <input
              type="hidden"
              ref={register()}
              name={`lineItems[${index}].costPerUnit`}
              defaultValue={lineItem.costPerUnit}
            />
            <input
              type="hidden"
              ref={register()}
              name={`lineItems[${index}].quantity`}
              defaultValue={lineItem.quantity}
            />
            <input
              type="hidden"
              ref={register()}
              name={`lineItems[${index}].minutes`}
              defaultValue={lineItem.minutes}
            />
            <input
              type="hidden"
              ref={register()}
              name={`lineItems[${index}].customUnit`}
              defaultValue={lineItem.customUnit}
            />
            <input
              type="hidden"
              ref={register()}
              name={`lineItems[${index}].remove`}
              defaultValue={lineItem.remove}
            />
            <input
              type="hidden"
              ref={register()}
              name={`lineItems[${index}].discount.percentage`}
              defaultValue={lineItem.discount?.percentage}
            />
            <input
              type="hidden"
              ref={register()}
              name={`lineItems[${index}].discount.amount`}
              defaultValue={lineItem.discount?.amount}
            />
            <input
              type="hidden"
              ref={register()}
              name={`lineItems[${index}].discount.type`}
              defaultValue={lineItem.discount?.type}
            />
            <input
              type="hidden"
              ref={register()}
              name={`lineItems[${index}].discount.description`}
              defaultValue={lineItem.discount?.description}
            />
            <WSForm.Field
              hidden
              name={`lineItems[${index}].reimbursableExpense`}
              component={WSCheckboxToggle}
              componentProps={{ label: "Reimbursable expense" }}
              defaultValue={lineItem.reimbursableExpense as any}
            />
            <WSForm.Field
              hidden
              key={memberClient?.integration?.quickbooks?.itemId}
              name={`lineItems[${index}].integration.quickbooks.itemId`}
              component={WSTextInput}
              defaultValue={
                lineItem.integration?.quickbooks?.itemId ||
                quickbooksDefaultItemId
              }
            />
            <WSForm.Field
              hidden
              key={memberClient?.integration?.quickbooks?.expenseAccountId}
              name={`lineItems[${index}].integration.quickbooks.expenseAccountId`}
              component={WSTextInput}
              defaultValue={
                lineItem.integration?.quickbooks?.expenseAccountId ||
                quickbooksDefaultAccountId
              }
            />

            {/*TODO: add fields*/}
            <WSFlexGrid container>
              <WSForm.Value name="lineItems">
                {items => {
                  const item = items[index];

                  return (
                    <>
                      <WSFlexGrid item mobile="6">
                        <WSText.Label>{item?.description}</WSText.Label>
                      </WSFlexGrid>
                      <WSFlexGrid item mobile="4">
                        <WSText.Label>
                          {formatMoney(item?.totalCost)}
                        </WSText.Label>
                      </WSFlexGrid>
                    </>
                  );
                }}
              </WSForm.Value>
              <WSFlexGrid item mobile="2">
                <WSButton.Link
                  type="button"
                  onClick={() =>
                    openModal("line-item", {
                      index,
                      lineItem
                    })
                  }
                >
                  Edit
                </WSButton.Link>
              </WSFlexGrid>
            </WSFlexGrid>
          </WSElement>
        ))}
      </WSScreen.MobileAndTablet>
      <WSScreen.Desktop>
        {lineItems.map((lineItem, index) => (
          <LineItemFormDesktop
            key={`${index}_${lineItem.id}`}
            index={index}
            defaultValues={lineItem as any}
            onRemove={
              lineItems.length > 1
                ? removeIndex => {
                    remove(removeIndex);
                    insert(index, { ...lineItem, remove: "1" });
                  }
                : undefined
            }
            onRateChange={() => {
              const values = getValues();

              if (values.lineItems[index].rate !== "fixed") {
                setValue(
                  `lineItems[${index}].totalCost`,
                  calculateAmount(values.lineItems[index])
                );
              }
            }}
            mb="M"
            hidden={!!lineItem.remove}
            {...quickbooksProps}
          />
        ))}
      </WSScreen.Desktop>

      <WSButton.Link
        mt="S"
        type="button"
        icon="circlePlus"
        onClick={() => {
          if (isDesktop) {
            append({
              description: "",
              totalCost: "",
              rate: "fixed",
              costPerUnit: "",
              quantity: "",
              customUnit: "",
              discount: {
                amount: "",
                percentage: "",
                type: "percentage",
                description: ""
              },
              integration: {
                quickbooks: {
                  expenseAccountId: quickbooksDefaultAccountId,
                  itemId: quickbooksDefaultItemId
                }
              },
              reimbursableExpense: false
            });
          } else {
            openModal("line-item");
          }
        }}
        name="addLineItem"
      >
        Add item
      </WSButton.Link>

      <WSForm.Error name="lineItems" />

      <WSDivider mt="S" mb="S" />

      <WSForm.Value name="lineItems">
        {(lineItems: LineItemValues[]) => {
          const total = calculateTotalAmount(lineItems);

          return (
            <>
              <WSElement>
                <WSFlexGrid container spacing="S">
                  <WSFlexGrid item mobile="6" tablet="7">
                    <WSText.Label color="grey600">Amount due</WSText.Label>
                  </WSFlexGrid>
                  <WSFlexGrid item mobile="6" tablet="5">
                    <WSText.Heading3>
                      {total ? formatMoney(total) : "$—.——"}
                    </WSText.Heading3>
                  </WSFlexGrid>
                </WSFlexGrid>
              </WSElement>
            </>
          );
        }}
      </WSForm.Value>
    </WSElement>
  );
};

type LineItemFormDesktopProps = {
  index: number;
  defaultValues?: LineItemValues;
  onRemove?: (index: number) => void;
  onRateChange: (index: number) => void;
  isQuickbooksItem?: boolean;
  isQuickbooksAccount?: boolean;
  quickbooksOptions?: SelectOption[];
  quickbooksDefaultItemId?: string;
  quickbooksDefaultAccountId?: string;
} & WSElementProps;

export const LineItemFormDesktop: React.FC<LineItemFormDesktopProps> = ({
  index,
  defaultValues,
  onRemove,
  onRateChange,
  quickbooksOptions = [],
  isQuickbooksItem,
  isQuickbooksAccount,
  quickbooksDefaultItemId,
  quickbooksDefaultAccountId,
  ...elementProps
}) => {
  const { openModal } = useModalContext();

  const [rateVisible, setRateVisible] = useState(
    defaultValues?.rate !== "fixed"
  );
  const [discountVisible, setDiscountVisible] = useState(false);
  const { register } = useFormContext();
  const [showIntegrations, setShowIntegrations] = useState(false);

  return (
    <WSElement {...elementProps}>
      <WSFlexGrid container spacing="S">
        <WSFlexGrid item mobile="6" tablet="7">
          <WSForm.Field
            name={`lineItems[${index}].description`}
            component={WSTextInput}
            defaultValue={defaultValues?.description || ""}
          />
        </WSFlexGrid>
        <WSFlexGrid item mobile="6" tablet="5">
          <WSFlexBox.CenterY
            wrap="nowrap"
            className={styles.totalCostFieldContainer}
          >
            <WSElement
              className={styles.totalCostField}
              mr={onRemove ? "S" : undefined}
            >
              <WSForm.Value name={`lineItems[${index}].rate`}>
                {rate => {
                  return (
                    <>
                      <WSForm.Field
                        name={`lineItems[${index}].totalCost`}
                        hidden={
                          rate === "hourly" ||
                          rate === "quantity" ||
                          rate === "other"
                        }
                        component={WSTextInput}
                        componentProps={{
                          prefix: "$",
                          formatOptions: {
                            numeral: true,
                            numeralThousandsGroupStyle: "thousand",
                            rawValueTrimPrefix: true
                          }
                        }}
                        defaultValue={defaultValues?.totalCost || ""}
                      />
                      {(rate === "hourly" ||
                        rate === "quantity" ||
                        rate === "other") && (
                        <WSText.Heading3>
                          <WSForm.Value name={`lineItems[${index}].totalCost`}>
                            {totalCost => formatMoney(totalCost)}
                          </WSForm.Value>
                        </WSText.Heading3>
                      )}
                    </>
                  );
                }}
              </WSForm.Value>
            </WSElement>
            {onRemove && (
              <WSIcon
                name="circleMinus"
                onClick={() => {
                  onRemove(index);
                }}
                color="blue"
              />
            )}
          </WSFlexBox.CenterY>
        </WSFlexGrid>
      </WSFlexGrid>

      <WSElement mt="S">
        <WSFlexGrid container spacing="S">
          <WSFlexGrid item mobile="6" tablet="7">
            <WSElement
              style={{ display: rateVisible ? "block" : "none" }}
              mb="S"
            >
              <WSFlexBox>
                <WSForm.Field
                  label="Rate"
                  name={`lineItems[${index}].rate`}
                  component={WSSelect}
                  componentProps={{
                    onChange: onRateChange,
                    options: [
                      {
                        value: "fixed",
                        label: "Fixed"
                      },
                      {
                        value: "hourly",
                        label: "Hourly"
                      },
                      {
                        value: "quantity",
                        label: "Quantity"
                      }
                    ]
                  }}
                  mr="S"
                  className={styles.rateField}
                  defaultValue={defaultValues?.rate || "fixed"}
                />
                <input
                  type="hidden"
                  ref={register()}
                  name={`lineItems[${index}].remove`}
                  defaultValue={defaultValues?.remove}
                />
                <WSForm.Value name={`lineItems[${index}].rate`}>
                  {rate => {
                    return (
                      <>
                        <WSForm.Field
                          label="Custom Rate"
                          hidden={rate !== "other"}
                          name={`lineItems[${index}].customUnit`}
                          component={WSTextInput}
                          componentProps={{
                            onChange: onRateChange
                          }}
                          mr="S"
                          className={styles.customUnitField}
                          defaultValue={defaultValues?.customUnit || ""}
                        />

                        <WSForm.Value name={`lineItems[${index}].customUnit`}>
                          {customUnit => {
                            const unit =
                              rate === "hourly"
                                ? "Hour"
                                : rate === "quantity"
                                ? "Unit"
                                : customUnit;

                            return (
                              <>
                                <WSForm.Field
                                  hidden={rate === "fixed"}
                                  label={`Amount per ${unit || "Item"}`}
                                  name={`lineItems[${index}].costPerUnit`}
                                  component={WSTextInput}
                                  componentProps={{
                                    onChange: onRateChange,
                                    prefix: "$",
                                    formatOptions: {
                                      numeral: true
                                    }
                                  }}
                                  mr="S"
                                  className={styles.costPerUnitField}
                                  defaultValue={
                                    defaultValues?.costPerUnit || ""
                                  }
                                />

                                <WSForm.Field
                                  hidden={rate === "fixed"}
                                  label={
                                    rate === "hourly"
                                      ? "Hours"
                                      : unit
                                      ? `${unit}(s)`
                                      : "Items"
                                  }
                                  name={`lineItems[${index}].quantity`}
                                  component={WSTextInput}
                                  componentProps={{
                                    onChange: onRateChange,
                                    formatOptions: {
                                      numericOnly: true
                                    }
                                  }}
                                  mr={rate === "hourly" ? "S" : undefined}
                                  className={styles.quantityField}
                                  defaultValue={defaultValues?.quantity || ""}
                                />

                                <WSForm.Field
                                  hidden={rate !== "hourly"}
                                  label="Minutes"
                                  name={`lineItems[${index}].minutes`}
                                  component={WSTextInput}
                                  componentProps={{
                                    onChange: onRateChange,
                                    formatOptions: {
                                      numericOnly: true
                                    }
                                  }}
                                  className={styles.quantityField}
                                  defaultValue={defaultValues?.minutes || ""}
                                />
                              </>
                            );
                          }}
                        </WSForm.Value>
                      </>
                    );
                  }}
                </WSForm.Value>
              </WSFlexBox>

              <WSForm.Field
                mt="S"
                name={`lineItems[${index}].reimbursableExpense`}
                component={WSCheckboxToggle}
                componentProps={{ label: "Reimbursable expense" }}
                defaultValue={defaultValues?.reimbursableExpense as any}
              />
            </WSElement>

            <WSButton.Link
              onClick={() => {
                setRateVisible(oldValue => !oldValue);
              }}
              type="button"
            >
              {rateVisible ? "Hide rate details" : "Show rate details"}
            </WSButton.Link>

            {isQuickbooksAccount || isQuickbooksItem ? (
              <>
                <WSElement
                  style={{ display: showIntegrations ? "block" : "none" }}
                >
                  <WSForm.Field
                    key={
                      quickbooksOptions.map(v => v.value)?.join("") +
                      quickbooksDefaultAccountId +
                      quickbooksDefaultItemId
                    }
                    label={
                      isQuickbooksItem
                        ? "Quickbooks item"
                        : "Quickbooks expense account"
                    }
                    name={
                      isQuickbooksItem
                        ? `lineItems[${index}].integration.quickbooks.itemId`
                        : `lineItems[${index}].integration.quickbooks.expenseAccountId`
                    }
                    component={WSSelect}
                    componentProps={{
                      options: quickbooksOptions,
                      searchable: true,
                      placeholderActions: [
                        {
                          label: "Resync QBO Items",
                          icon: "sync",
                          callback() {
                            openModal(INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT, {
                              entity: isQuickbooksItem
                                ? QUICKBOOKS_ENTITY.ITEMS
                                : QUICKBOOKS_ENTITY.EXPENSES
                            });
                          }
                        }
                      ]
                    }}
                    mt="S"
                    className={styles.quickbooksField}
                    defaultValue={
                      isQuickbooksItem
                        ? defaultValues?.integration?.quickbooks?.itemId ||
                          quickbooksDefaultItemId
                        : defaultValues?.integration?.quickbooks
                            ?.expenseAccountId || quickbooksDefaultAccountId
                    }
                  />
                </WSElement>
                <WSButton.Link
                  onClick={() => {
                    setShowIntegrations(v => !v);
                  }}
                  type="button"
                  mb="S"
                  mt="S"
                >
                  {showIntegrations ? "Hide integrations" : "Show integrations"}
                </WSButton.Link>
              </>
            ) : null}
          </WSFlexGrid>
          <WSFlexGrid item mobile="6" tablet="5">
            <WSElement
              style={{ display: discountVisible ? "block" : "none" }}
              mb="S"
            >
              <WSForm.Label>Discount</WSForm.Label>
              <WSFlexBox wrap="nowrap" mb="M">
                <WSForm.Value name={`lineItems[${index}].discount.type`}>
                  {type => {
                    if (type === "percentage") {
                      return (
                        <WSForm.Field
                          key="percentage"
                          className={styles.smallField}
                          name={`lineItems[${index}].discount.percentage`}
                          component={WSTextInput}
                          componentProps={{
                            postfix: "%",
                            formatOptions: {
                              numeral: true
                            }
                          }}
                          mr="S"
                          defaultValue={defaultValues?.discount.percentage}
                        />
                      );
                    } else if (type === "amount") {
                      return (
                        <WSForm.Field
                          key="amount"
                          className={styles.smallField}
                          name={`lineItems[${index}].discount.amount`}
                          component={WSTextInput}
                          componentProps={{
                            prefix: "$",
                            formatOptions: {
                              numeral: true
                            }
                          }}
                          mr="S"
                          defaultValue={defaultValues?.discount.amount}
                        />
                      );
                    } else {
                      return null;
                    }
                  }}
                </WSForm.Value>
                <WSForm.Field
                  className={styles.typeField}
                  name={`lineItems[${index}].discount.type`}
                  component={WSSelect}
                  componentProps={{
                    options: [
                      {
                        value: "percentage",
                        label: "% of item"
                      },
                      {
                        value: "amount",
                        label: "fixed amount"
                      }
                    ]
                  }}
                  defaultValue={defaultValues?.discount.type}
                />
              </WSFlexBox>

              <WSForm.Field
                label="Discount description"
                name={`lineItems[${index}].discount.description`}
                component={WSTextInput}
                defaultValue={defaultValues?.discount.description}
              />
            </WSElement>

            <WSForm.Value name={`lineItems[${index}].discount`}>
              {discount =>
                discountVisible ? (
                  <WSButton.Link
                    onClick={() => {
                      setDiscountVisible(false);
                    }}
                    type="button"
                  >
                    Apply discount
                  </WSButton.Link>
                ) : discount &&
                  discount.type === "amount" &&
                  !!discount.amount ? (
                  <WSText>
                    {toWSMoneyString(discount.amount)} discount applied.{" "}
                    <WSButton.Link
                      inline
                      onClick={() => {
                        setDiscountVisible(true);
                      }}
                      type="button"
                    >
                      Change
                    </WSButton.Link>
                  </WSText>
                ) : discount &&
                  discount.type === "percentage" &&
                  !!discount.percentage ? (
                  <WSText>
                    {discount.percentage}% discount applied.{" "}
                    <WSButton.Link
                      inline
                      onClick={() => {
                        setDiscountVisible(true);
                      }}
                      type="button"
                    >
                      Change
                    </WSButton.Link>
                  </WSText>
                ) : (
                  <WSButton.Link
                    onClick={() => {
                      setDiscountVisible(true);
                    }}
                    type="button"
                  >
                    Apply discount
                  </WSButton.Link>
                )
              }
            </WSForm.Value>
          </WSFlexGrid>
        </WSFlexGrid>
      </WSElement>
    </WSElement>
  );
};

type LineItemFormMobileProps = {
  defaultValues?: LineItemValues;
  onSubmit: (values: LineItemValues) => void;
  onCancel: () => void;
  isQuickbooksItem?: boolean;
  isQuickbooksAccount?: boolean;
  quickbooksDefaultItemId?: string;
  quickbooksDefaultAccountId?: string;
  quickbooksOptions?: SelectOption[];
};

export const LineItemFormMobile: React.FC<LineItemFormMobileProps> = ({
  defaultValues,
  onCancel,
  onSubmit,
  isQuickbooksItem,
  quickbooksDefaultItemId,
  quickbooksDefaultAccountId,
  isQuickbooksAccount,
  quickbooksOptions = []
}) => {
  const { openModal } = useModalContext();

  const [rateVisible, setRateVisible] = useState(
    defaultValues?.rate === "hourly" ||
      defaultValues?.rate === "quantity" ||
      defaultValues?.rate === "other"
  );
  const [discountVisible, setDiscountVisible] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);

  return (
    <>
      <WSText.Heading3 mb="S">
        {defaultValues ? "Update item" : "New item"}
      </WSText.Heading3>
      <WSForm<LineItemValues>
        defaultValues={
          defaultValues || {
            description: "",
            totalCost: "",
            rate: "fixed",
            costPerUnit: "",
            quantity: "",
            customUnit: "",
            discount: {
              amount: "",
              percentage: "",
              type: "percentage",
              description: ""
            },
            reimbursableExpense: false,
            integration: {
              quickbooks: {
                itemId: quickbooksDefaultItemId,
                expenseAccountId: quickbooksDefaultAccountId
              }
            }
          }
        }
        validationSchema={lineItemValidationSchema}
      >
        {({ handleSubmit, getValues, setValue, register }) => {
          const calculateTotalCost = () => {
            const values = getValues();

            if (values.rate !== "fixed") {
              setValue("totalCost", calculateAmount(values));
            }
          };

          return (
            <>
              <input
                type="hidden"
                ref={register()}
                name="remove"
                defaultValue={defaultValues?.remove}
              />

              <WSForm.Field
                mb="S"
                name="description"
                label="Description"
                component={WSTextInput}
              />
              <WSForm.Value name="rate">
                {rate => (
                  <>
                    <WSForm.Field
                      hidden={
                        rate === "hourly" ||
                        rate === "quantity" ||
                        rate === "other"
                      }
                      mb="S"
                      name="totalCost"
                      label="Amount"
                      component={WSTextInput}
                      componentProps={{
                        prefix: "$",
                        formatOptions: {
                          numeral: true,
                          numeralThousandsGroupStyle: "thousand",
                          rawValueTrimPrefix: true
                        }
                      }}
                      defaultValue={defaultValues?.totalCost || ""}
                    />
                    {(rate === "hourly" ||
                      rate === "quantity" ||
                      rate === "other") && (
                      <WSText.Heading3 mb="S">
                        <WSForm.Value name="totalCost">
                          {totalCost => formatMoney(totalCost)}
                        </WSForm.Value>
                      </WSText.Heading3>
                    )}
                  </>
                )}
              </WSForm.Value>

              <WSElement mb="M">
                <WSElement
                  style={{ display: rateVisible ? "block" : "none" }}
                  mb="S"
                >
                  <WSForm.Field
                    label="Rate"
                    name="rate"
                    component={WSSelect}
                    componentProps={{
                      options: [
                        {
                          value: "fixed",
                          label: "Fixed"
                        },
                        {
                          value: "hourly",
                          label: "Hourly"
                        },
                        {
                          value: "quantity",
                          label: "Quantity"
                        }
                      ],
                      onChange: calculateTotalCost
                    }}
                    mb="S"
                  />
                  <WSForm.Value name="rate">
                    {rate => {
                      return (
                        <>
                          <WSForm.Field
                            label="Custom Rate"
                            hidden={rate !== "other"}
                            name="customUnit"
                            component={WSTextInput}
                            componentProps={{
                              onChange: calculateTotalCost
                            }}
                            mb="S"
                          />

                          <WSForm.Value name="customUnit">
                            {customUnit => {
                              const unit =
                                rate === "hourly"
                                  ? "Hour"
                                  : rate === "quantity"
                                  ? "Unit"
                                  : customUnit;

                              return (
                                <>
                                  <WSForm.Field
                                    hidden={rate === "fixed"}
                                    label={`Amount per ${unit || "Item"}`}
                                    name="costPerUnit"
                                    component={WSTextInput}
                                    componentProps={{
                                      onChange: calculateTotalCost,
                                      prefix: "$",
                                      formatOptions: {
                                        numeral: true
                                      }
                                    }}
                                    mb="S"
                                  />

                                  <WSForm.Field
                                    hidden={rate === "fixed"}
                                    label={
                                      rate === "hourly"
                                        ? "Hours"
                                        : unit
                                        ? `${unit}(s)`
                                        : "Items"
                                    }
                                    name="quantity"
                                    component={WSTextInput}
                                    componentProps={{
                                      onChange: calculateTotalCost,
                                      formatOptions: {
                                        formatOptions:
                                          rate === "hourly"
                                            ? {
                                                numericOnly: true
                                              }
                                            : {
                                                numeral: true
                                              }
                                      }
                                    }}
                                    mb={rate === "hourly" ? "S" : undefined}
                                  />

                                  <WSForm.Field
                                    hidden={rate !== "hourly"}
                                    label="Minutes"
                                    name="minutes"
                                    component={WSTextInput}
                                    componentProps={{
                                      onChange: calculateTotalCost,
                                      formatOptions: {
                                        numericOnly: true
                                      }
                                    }}
                                    defaultValue={defaultValues?.minutes || ""}
                                  />
                                </>
                              );
                            }}
                          </WSForm.Value>
                        </>
                      );
                    }}
                  </WSForm.Value>
                  <WSForm.Field
                    mt="S"
                    name="reimbursableExpense"
                    component={WSCheckboxToggle}
                    componentProps={{ label: "Reimbursable expense" }}
                    defaultValue={defaultValues?.reimbursableExpense as any}
                  />
                </WSElement>

                <WSButton.Link
                  onClick={() => {
                    setRateVisible(!rateVisible);
                  }}
                  type="button"
                  mb="S"
                >
                  {rateVisible ? "Hide rate details" : "Show rate details"}
                </WSButton.Link>

                {isQuickbooksAccount || isQuickbooksItem ? (
                  <>
                    <WSElement
                      style={{ display: showIntegrations ? "block" : "none" }}
                    >
                      <WSForm.Field
                        key={
                          quickbooksOptions.map(v => v.value)?.join("") +
                          quickbooksDefaultAccountId +
                          quickbooksDefaultItemId
                        }
                        label={
                          isQuickbooksItem
                            ? "Quickbooks item"
                            : "Quickbooks expense account"
                        }
                        name={
                          isQuickbooksItem
                            ? `integration.quickbooks.itemId`
                            : `integration.quickbooks.expenseAccountId`
                        }
                        component={WSSelect}
                        componentProps={{
                          options: quickbooksOptions,
                          searchable: true,
                          placeholderActions: [
                            {
                              label: "Resync QBO Items",
                              icon: "sync",
                              callback() {
                                openModal(
                                  INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT,
                                  {
                                    entity: isQuickbooksItem
                                      ? QUICKBOOKS_ENTITY.ITEMS
                                      : QUICKBOOKS_ENTITY.EXPENSES
                                  }
                                );
                              }
                            }
                          ]
                        }}
                        mt="S"
                        className={styles.quickbooksField}
                        defaultValue={
                          isQuickbooksItem
                            ? defaultValues?.integration?.quickbooks?.itemId ||
                              quickbooksDefaultItemId
                            : defaultValues?.integration?.quickbooks
                                ?.expenseAccountId || quickbooksDefaultAccountId
                        }
                      />
                    </WSElement>
                    <WSButton.Link
                      onClick={() => {
                        setShowIntegrations(v => !v);
                      }}
                      type="button"
                      mb="S"
                      mt="S"
                    >
                      {showIntegrations
                        ? "Hide integrations"
                        : "Show integrations"}
                    </WSButton.Link>
                  </>
                ) : null}

                <WSElement
                  style={{ display: discountVisible ? "block" : "none" }}
                  mb="S"
                >
                  <WSForm.Label>Discount</WSForm.Label>
                  <WSFlexBox wrap="nowrap" mb="M">
                    <WSForm.Value name="discount.type">
                      {type => {
                        if (type === "percentage") {
                          return (
                            <WSForm.Field
                              key="percentage"
                              className={styles.smallField}
                              name="discount.percentage"
                              component={WSTextInput}
                              componentProps={{
                                postfix: "%",
                                formatOptions: {
                                  numeral: true
                                }
                              }}
                              mr="S"
                              defaultValue={defaultValues?.discount.percentage}
                            />
                          );
                        } else if (type === "amount") {
                          return (
                            <WSForm.Field
                              key="amount"
                              className={styles.smallField}
                              name="discount.amount"
                              component={WSTextInput}
                              componentProps={{
                                prefix: "$",
                                formatOptions: {
                                  numeral: true,
                                  numeralThousandsGroupStyle: "thousand",
                                  rawValueTrimPrefix: true
                                }
                              }}
                              mr="S"
                              defaultValue={defaultValues?.discount.amount}
                            />
                          );
                        } else {
                          return null;
                        }
                      }}
                    </WSForm.Value>
                    <WSForm.Field
                      className={styles.typeField}
                      name="discount.type"
                      component={WSSelect}
                      componentProps={{
                        options: [
                          {
                            value: "percentage",
                            label: "% of item"
                          },
                          {
                            value: "amount",
                            label: "fixed amount"
                          }
                        ]
                      }}
                      defaultValue={defaultValues?.discount.type}
                    />
                  </WSFlexBox>

                  <WSForm.Field
                    label="Discount description"
                    name="discount.description"
                    component={WSTextInput}
                    defaultValue={defaultValues?.discount.description}
                  />
                </WSElement>

                <WSForm.Value name="discount">
                  {discount =>
                    discountVisible ? (
                      <WSButton.Link
                        onClick={() => {
                          setDiscountVisible(false);
                        }}
                        type="button"
                      >
                        Apply discount
                      </WSButton.Link>
                    ) : discount &&
                      discount.type === "amount" &&
                      !!discount.amount ? (
                      <WSText>
                        {toWSMoneyString(discount.amount)} discount applied.{" "}
                        <WSButton.Link
                          inline
                          onClick={() => {
                            setDiscountVisible(true);
                          }}
                          type="button"
                        >
                          Change
                        </WSButton.Link>
                      </WSText>
                    ) : discount &&
                      discount.type === "percentage" &&
                      !!discount.percentage ? (
                      <WSText>
                        {discount.percentage}% discount applied.{" "}
                        <WSButton.Link
                          inline
                          onClick={() => {
                            setDiscountVisible(true);
                          }}
                          type="button"
                        >
                          Change
                        </WSButton.Link>
                      </WSText>
                    ) : (
                      <WSButton.Link
                        onClick={() => {
                          setDiscountVisible(true);
                        }}
                        type="button"
                      >
                        Apply discount
                      </WSButton.Link>
                    )
                  }
                </WSForm.Value>
              </WSElement>

              <WSButtons mt="M" format="modal">
                <WSButton.Primary
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                >
                  Save
                </WSButton.Primary>
                <WSButton.Tertiary onClick={onCancel}>Cancel</WSButton.Tertiary>
                {defaultValues && (
                  <WSButton.Link
                    color="coral"
                    type="button"
                    onClick={() => {
                      setValue("remove", "1");
                      // Skip validation and submit
                      onSubmit(getValues());
                    }}
                  >
                    Delete item
                  </WSButton.Link>
                )}
              </WSButtons>
            </>
          );
        }}
      </WSForm>
    </>
  );
};
